import { useCallback, useEffect, useRef, useState } from 'react';
import { hasValue, isFunction, isNil } from 'swipeclock-frontend';

const OFFLINE_PUNCH_STORAGE_UNAVAILABLE_MESSAGE =
  'Offline punch storage is unavailable. Please contact your administrator.';

const showStorageError = (setSnackbar) => {
  if (!isFunction(setSnackbar)) {
    return;
  }

  setSnackbar({
    key: 'offline-punch-delete-failed',
    message: OFFLINE_PUNCH_STORAGE_UNAVAILABLE_MESSAGE,
    severity: 'error',
  });
};

export function useOfflinePunchDeleteConfirmation({
  deletePunch,
  setSnackbar,
  visiblePunchIds = [],
}) {
  const [pendingPunchId, setPendingPunchId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);

  useEffect(() => {
    if (isDeleting || isNil(pendingPunchId)) {
      return;
    }

    const isStillVisible =
      Array.isArray(visiblePunchIds) &&
      visiblePunchIds.includes(pendingPunchId);

    if (!isStillVisible) {
      setPendingPunchId(null);
    }
  }, [isDeleting, pendingPunchId, visiblePunchIds]);

  const requestDelete = useCallback(
    (itemId) => {
      if (isDeletingRef.current || hasValue(pendingPunchId)) {
        return;
      }

      setPendingPunchId(itemId);
    },
    [pendingPunchId]
  );

  const cancelDelete = useCallback(() => {
    if (isDeletingRef.current) {
      return;
    }

    setPendingPunchId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (isNil(pendingPunchId) || isDeletingRef.current) {
      return;
    }

    isDeletingRef.current = true;
    setIsDeleting(true);

    try {
      const saved = await deletePunch(pendingPunchId);

      if (!saved) {
        showStorageError(setSnackbar);
      }
    } catch {
      showStorageError(setSnackbar);
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
      setPendingPunchId(null);
    }
  }, [deletePunch, pendingPunchId, setSnackbar]);

  const isDeleteDisabled = useCallback(
    () => isDeleting || hasValue(pendingPunchId),
    [isDeleting, pendingPunchId]
  );

  return {
    pendingPunchId,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
    isDeleteDisabled,
  };
}
